import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from './PageHeader';
import { createLocalBranch, fetchCompanies, listLocalBranches } from '../utils/api';
import { formatDate } from '../utils/dateUtils';

const PRIMARY = '#40518A';

const emptyForm = {
  branch_id: '',
  company_id: '',
  branch_name: '',
  branch_address: '',
};

const BranchConfiguration = () => {
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadBranches = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listLocalBranches({ limit: 500 });
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load branches', 'error');
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const data = await fetchCompanies(200, 0, false);
      setCompanies(Array.isArray(data?.companies) ? data.companies : []);
    } catch (err) {
      showSnackbar(err?.message || 'Failed to load companies', 'error');
      setCompanies([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
    loadCompanies();
  }, [loadBranches, loadCompanies]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const branchId = form.branch_id.trim();
    const companyId = form.company_id.trim();
    const branchName = form.branch_name.trim();
    const branchAddress = form.branch_address.trim();
    if (!branchId || !companyId || !branchName) {
      showSnackbar('Branch ID, company, and branch name are required', 'error');
      return;
    }
    if (branchId.length > 36) {
      showSnackbar('Branch ID must be at most 36 characters', 'error');
      return;
    }
    setSaving(true);
    try {
      await createLocalBranch({
        branchId,
        companyId,
        branchName,
        branchAddress,
      });
      setDialogOpen(false);
      setForm(emptyForm);
      showSnackbar('Branch created');
      await loadBranches();
    } catch (err) {
      showSnackbar(err?.message || 'Failed to create branch', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Branch Configuration"
        subtitle="Create and view branches"
      />
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#334272' } }}
        >
          Create Branch
        </Button>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', overflowX: { xs: 'hidden', sm: 'auto' }, WebkitOverflowScrolling: 'touch' }}
        >
          <Table size="medium" sx={{ minWidth: { xs: 0, sm: 720 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Branch ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Branch Name</TableCell>
                <TableCell>Branch Address</TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No branches found.
                  </TableCell>
                </TableRow>
              ) : (
                branches.map((branch) => {
                  const createdByName = (branch.created_by_name || branch.created_by || '').trim();
                  const createdAt = formatDate(branch.created_at);
                  const hasCreatedBy = Boolean(createdByName);
                  const hasCreatedAt = Boolean(createdAt && createdAt !== '-');
                  return (
                    <TableRow key={branch.branch_id}>
                      <TableCell>{branch.branch_id}</TableCell>
                      <TableCell>{branch.company_name || branch.company_id}</TableCell>
                      <TableCell>{branch.branch_name || '—'}</TableCell>
                      <TableCell>{branch.branch_address || '—'}</TableCell>
                      <TableCell>
                        {!hasCreatedBy && !hasCreatedAt ? (
                          <span style={{ color: '#999' }}>—</span>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {hasCreatedBy ? createdByName : '—'}
                            </Typography>
                            {hasCreatedAt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {createdAt}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Branch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Branch ID"
              value={form.branch_id}
              onChange={(e) => setForm((prev) => ({ ...prev, branch_id: e.target.value }))}
              required
              inputProps={{ maxLength: 5 }}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel id="branch-company-label">Company</InputLabel>
              <Select
                labelId="branch-company-label"
                label="Company"
                value={form.company_id}
                onChange={(e) => setForm((prev) => ({ ...prev, company_id: e.target.value }))}
              >
                {companies.length === 0 ? (
                  <MenuItem value="" disabled>No companies found</MenuItem>
                ) : (
                  companies.map((company) => (
                    <MenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              label="Branch Name"
              value={form.branch_name}
              onChange={(e) => setForm((prev) => ({ ...prev, branch_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Branch Address"
              value={form.branch_address}
              onChange={(e) => setForm((prev) => ({ ...prev, branch_address: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving} sx={{ bgcolor: PRIMARY }}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BranchConfiguration;
